import PropTypes from 'prop-types';
import React, { Component } from 'react';
import Errored from '../../core/Errored';
import Loading from '../../core/Loading';
import { makeCancelable, sortObjects } from '../../core/utils/utils';
import GS from '../../grafsync/grafsync';
import CollectionConcept from './CollectionConcept';

const CONCEPT_BATCH = 100;

const loadOptions = {
  predicates: [
    "http://www.w3.org/2004/02/skos/core#inScheme",
    "http://www.w3.org/2004/02/skos/core#prefLabel",
    "http://www.w3.org/2002/07/owl#deprecated",
    "http://www.w3.org/2008/05/skos-xl#prefLabel",
    "http://www.w3.org/1999/02/22-rdf-syntax-ns#type"
  ],
  includeAuth: true,
  tests: [
    {
      id: "http://www.w3.org/2008/05/skos-xl#prefLabel",
      join: [{
        predicate: "http://www.w3.org/2008/05/skos-xl#literalForm"
      }]
    }
  ]
};

class CollectionStructure extends Component {

  constructor(props) {
    super(props);
    this.state = {
      showNumber: CONCEPT_BATCH,
      isLoading: true,
      isLoaded: props.concepts && props.concepts.length > 0 ? false : true,
      isErrored: null,
      concepts: [],
      version: null
    };
    this.cancellable = null;
  }

  componentDidMount() {
    this.collectData();
  }

  timeMoreConcepts = () => {
    if (this.moreTimeout) clearTimeout(this.moreTimeout);
    if (this.state.showNumber < this.state.concepts.length) {
      this.moreTimeout = setTimeout(() => {
        this.showMore();
      }, 1000);
    }
  }

  static getDerivedStateFromProps(props, state) {
    if (props.concepts && props.version !== state.version) {
      return {
        isLoading: true,
        isLoaded: false,
        version: props.version
      };
    }
    return null;
  }

  componentDidUpdate(prevProps, prevState) {
    if (!this.state.isLoaded && this.props.concepts) this.collectData();
    this.timeMoreConcepts();
  }

  collectData() {
    // We load ALL the concepts necessary at this level in the tree in one API call to avoid unnecessary calls
    if (!this.state.isLoaded) {
      if (this.props.concepts.length > 0) {
        this.loadData(this.props.concepts)
          .promise
          .then(() => {
            if (this.cancellable && this.cancellable.isCancelled()) return;
            this.sortConcepts();
          })
          .catch((e) => {
            if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
          });
      }
      else {
        this.setState({ isLoading: false, isLoaded: true });
      }
    }
    else {
      this.setState({ isLoading: false });
    }
  }

  sortConcepts() {
    if (!this.props.ordered) {
      const concepts = this.props.concepts.map(id => GS.data(id, this.props.graph));
      const firstLang = this.props.viewLang || this.props.defaultLang;
      const secondLang = this.props.viewLang !== this.props.defaultLang ? this.props.defaultLang : null;
      let sortedConcepts = sortObjects([...concepts], 'prefLabel', firstLang, secondLang, false)
        .map(concept => concept.id);
      this.setState({ isLoading: false, isLoaded: true, concepts: sortedConcepts, showNumber: CONCEPT_BATCH });
    }
    else {
      this.setState({ isLoading: false, isLoaded: true, concepts: this.props.concepts, showNumber: CONCEPT_BATCH });
    }
  }

  loadData(iris) {
    const { viewLang, lang, graph } = this.props;
    const mergedLoadOptions = Object.assign({}, loadOptions, { language: viewLang ? [lang, viewLang] : lang });
    this.cancellable = makeCancelable(GS.loadData(iris, mergedLoadOptions, graph));
    return this.cancellable;
  }

  componentWillUnmount() {
    if (this.cancellable) this.cancellable.cancel();
    if (this.moreTimeout) clearTimeout(this.moreTimeout);
  }

  moveConcept = (id, idMovedTo) => {
    let sourceIndex;
    let targetIndex;
    this.props.concepts.forEach((concept, i) => {
      if (concept === id) sourceIndex = i;
      if (concept === idMovedTo) targetIndex = i;
    });
    GS.move(this.props.id, this.props.graph, 'memberList', sourceIndex, targetIndex);
  }

  showMore = () => {
    const showNumber = this.state.showNumber + CONCEPT_BATCH;
    this.setState({ showNumber });
  }

  render() {

    const { isLoading, isErrored, concepts, showNumber } = this.state;
    const { onSelect, lang, onBreakLink, isReadOnly, viewLang, graph, activeConcept, ordered } = this.props;

    if (isErrored) return <Errored data={isErrored} />;
    if (isLoading) return <Loading />;

    return (
      <React.Fragment>
        {concepts.slice(0, showNumber).map((concept, i) => 
          <CollectionConcept
            key={concept}
            id={concept}
            graph={graph}
            lang={lang}
            ordered={ordered}
            activeConcept={activeConcept}
            viewLang={viewLang}
            isReadOnly={isReadOnly}
            position={i}
            onSelect={onSelect}
            loadOptions={{
              language: viewLang && viewLang !== lang ? [lang, viewLang] : lang
            }}
            onBreakLink={() => onBreakLink(i, concept)}
            onMove={this.moveConcept} />
        )}
      </React.Fragment>
    );
  }

}

CollectionStructure.propTypes = {
  onBreakLink: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  viewLang: PropTypes.string,
  ordered: PropTypes.bool.isRequired,
  activeConcept: PropTypes.string,
  lang: PropTypes.string.isRequired,
  graph: PropTypes.string.isRequired,
  isReadOnly: PropTypes.bool.isRequired,
  concepts: PropTypes.array,
  version: PropTypes.number
};

export default CollectionStructure;